import { isBefore, startOfDay, startOfTomorrow } from 'date-fns';
import * as Yup from 'yup';

export const ERROR_MESSAGES = {
  INVALID_EMAIL: 'Incorrect email',
  INVALID_URL: 'Please enter a valid website URL',
  INVALID_YEAR_FOUNDED: 'Please enter in YYYY format',
  YEAR_FOUNDED_DATE: 'Year Founded should be after 1990 until now',
  MIN_SELECT: 'Please select at least one option',
  INVALID_DATE: 'Please enter a valid date',
  INVALID_FIRST_FINANCING_DATE: 'Date must be on or after January 1st of the Year Founded',
  MIN_COMPETITORS: "Please add/select at least one competitor or 'Competitor Unknown'",
  DEAL_DATE_AFTER_FINANCING_DATE: 'Deal Date should be no earlier than the First Financing Date',
  DEAL_DATE_AFTER_PREVIOUS_DEAL_DATE: 'Deal Date must be later the Previous Subround Deal Date',
  ANNOUNCED_DATE_AFTER_DEAL_DATE: 'Announced Date should be no earlier than the Deal Date',
  INVALID_POSITIVE_MONEY: 'The value must be a positive number in USD million',
  INVALID_PERCENTAGE: 'Please enter a valid percentage',
  INVALID_EMPLOYEES: 'Please enter the number of employees',
  INVALID_CEO_NAME: 'CEO Name cannot be empty',
  INVALID_CEO_EDUCATION: 'CEO Education cannot be empty',
  MIN_INVESTORS: 'Add/Select at least one investor',
  PASSWORDS_MISMATCH: 'Confirm Password must match Password',
  required: (field: string) => `${field} is required`,
  min: (field: string, count: number) => `${field} must be at least ${count} characters long`,
  max: (field: string, count: number) => `${field} must be maximum ${count} characters long`,
} as const;

const REGEXPS = {
  NATURAL_NUMBER: /^[1-9]\d*$/,
  NUMBER_WITH_OPTIONAL_FLOATING_POINTS: /^(0(\.\d+)?|[1-9]\d*(\.\d+)?)$/,
  INTEGER: /^(0|[1-9]\d*)$/,
  WEB: /^(https?:\/\/)?(www\.)?\w+\.[a-zA-Z]{2,}(\/.*)?$/,
  WEB_WITH_SUBDOMAIN: /^(?!.*www\.[^.]*$).*\.([^.]+)$/,
};

const passwordSchema = Yup.string().required(ERROR_MESSAGES.required('Password'));
const emailSchema = Yup.string()
  .required(ERROR_MESSAGES.required('Email'))
  .email(ERROR_MESSAGES.INVALID_EMAIL);

export const autocompleteOptionSchema = Yup.object({
  label: Yup.string().required(),
  value: Yup.mixed<string | number>()
    .required()
    .test('string-or-number', '', value => typeof value === 'string' || typeof value === 'number'),
  meta: Yup.object().optional(),
});

export const loginFormSchema = Yup.object({
  email: emailSchema,
  password: passwordSchema,
});

export const companyNameSchema = Yup.string()
  .required(ERROR_MESSAGES.min('Company Name', 2))
  .transform(value => value.trim())
  .min(2, ERROR_MESSAGES.min('Company Name', 2))
  .max(255, ERROR_MESSAGES.max('Company Name', 255));

export const companyWebSchema = Yup.string()
  .required(ERROR_MESSAGES.required('Web'))
  .matches(REGEXPS.WEB, ERROR_MESSAGES.INVALID_URL)
  .test('ends-with-correct-tld', ERROR_MESSAGES.INVALID_URL, value => {
    return REGEXPS.WEB_WITH_SUBDOMAIN.test(value);
  });

export const createCompanyFormSchema = Yup.object({
  form: Yup.object({
    name: companyNameSchema,
    formerName: Yup.string()
      .default('')
      .defined()
      .max(255, ERROR_MESSAGES.max('Former Name', 255))
      .test(
        'min-2-if-exists',
        ERROR_MESSAGES.min('Former Name', 2),
        value => !value || value.length >= 2,
      ),
    web: companyWebSchema,
    pitchbookUrl: Yup.string().default('').defined(),
    foundedYear: Yup.string()
      .required(ERROR_MESSAGES.required('Year Founded'))
      .length(4, ERROR_MESSAGES.INVALID_YEAR_FOUNDED)
      .matches(REGEXPS.NATURAL_NUMBER, ERROR_MESSAGES.INVALID_YEAR_FOUNDED)
      .test(
        'after-1990-until-now',
        ERROR_MESSAGES.YEAR_FOUNDED_DATE,
        value => +value > 1990 && +value <= new Date().getFullYear(),
      ),
    industries: Yup.array()
      .of(autocompleteOptionSchema)
      .required(ERROR_MESSAGES.MIN_SELECT)
      .min(1, ERROR_MESSAGES.MIN_SELECT),
    firstFinancingDate: Yup.date()
      .defined()
      .nullable()
      .test('date-is-set', ERROR_MESSAGES.INVALID_DATE, value => value !== null)
      .test(
        'is-in-past',
        ERROR_MESSAGES.INVALID_DATE,
        value => !!value && isBefore(startOfDay(value), startOfTomorrow()),
      ),
    firstFinancingDealType: autocompleteOptionSchema
      .nullable()
      .test('value-is-set', ERROR_MESSAGES.MIN_SELECT, value => value !== null),
    bio: Yup.string().default('').defined().max(2000, ERROR_MESSAGES.max('Description', 2000)),
    competitors: Yup.array()
      .of(autocompleteOptionSchema)
      .required(ERROR_MESSAGES.MIN_COMPETITORS)
      .min(1, ERROR_MESSAGES.MIN_COMPETITORS),
    comments: Yup.string().default('').defined(),
    rounds: Yup.array()
      .of(
        Yup.object({
          name: Yup.string().required(),
          subrounds: Yup.array()
            .of(
              Yup.object({
                id: Yup.string().required(),
                dealDate: Yup.date()
                  .defined()
                  .nullable()
                  .test('date-is-set', ERROR_MESSAGES.INVALID_DATE, value => value !== null),
                announcedDate: Yup.date().defined().nullable(),
                dealSize: Yup.string()
                  .required(ERROR_MESSAGES.INVALID_POSITIVE_MONEY)
                  .matches(
                    REGEXPS.NUMBER_WITH_OPTIONAL_FLOATING_POINTS,
                    ERROR_MESSAGES.INVALID_POSITIVE_MONEY,
                  )
                  .test('is-positive', ERROR_MESSAGES.INVALID_POSITIVE_MONEY, value => +value > 0),
                securityType: autocompleteOptionSchema.default(null).nullable(),
                cap: Yup.string()
                  .default('')
                  .defined()
                  .test(
                    'number-with-optional-floating-points-if-exists',
                    ERROR_MESSAGES.INVALID_POSITIVE_MONEY,
                    value =>
                      value.length
                        ? REGEXPS.NUMBER_WITH_OPTIONAL_FLOATING_POINTS.test(value) && +value > 0
                        : true,
                  ),
                discount: Yup.string()
                  .default('')
                  .defined()
                  .test(
                    'number-with-optional-floating-points-between-0-and-100-if-exists',
                    ERROR_MESSAGES.INVALID_PERCENTAGE,
                    value =>
                      value.length
                        ? REGEXPS.NUMBER_WITH_OPTIONAL_FLOATING_POINTS.test(value) &&
                          +value >= 0 &&
                          +value <= 100
                        : true,
                  ),
                interest: Yup.string()
                  .default('')
                  .defined()
                  .test(
                    'number-with-optional-floating-points-between-0-and-100-if-exists',
                    ERROR_MESSAGES.INVALID_PERCENTAGE,
                    value =>
                      value.length
                        ? REGEXPS.NUMBER_WITH_OPTIONAL_FLOATING_POINTS.test(value) &&
                          +value >= 0 &&
                          +value <= 100
                        : true,
                  ),
                conversionAmount: Yup.string().default('').defined(),
                preMoneyValuation: Yup.string()
                  .required(ERROR_MESSAGES.INVALID_POSITIVE_MONEY)
                  .matches(
                    REGEXPS.NUMBER_WITH_OPTIONAL_FLOATING_POINTS,
                    ERROR_MESSAGES.INVALID_POSITIVE_MONEY,
                  )
                  .test('is-positive', ERROR_MESSAGES.INVALID_POSITIVE_MONEY, value => +value > 0),
                postMoneyValuation: Yup.string()
                  .required(ERROR_MESSAGES.INVALID_POSITIVE_MONEY)
                  .matches(
                    REGEXPS.NUMBER_WITH_OPTIONAL_FLOATING_POINTS,
                    ERROR_MESSAGES.INVALID_POSITIVE_MONEY,
                  )
                  .test('is-positive', ERROR_MESSAGES.INVALID_POSITIVE_MONEY, value => +value > 0),
                acquiredPercent: Yup.string()
                  .required(ERROR_MESSAGES.INVALID_PERCENTAGE)
                  .matches(
                    REGEXPS.NUMBER_WITH_OPTIONAL_FLOATING_POINTS,
                    ERROR_MESSAGES.INVALID_PERCENTAGE,
                  )
                  .test(
                    'is-between-0-and-100',
                    ERROR_MESSAGES.INVALID_PERCENTAGE,
                    value => +value >= 0 && +value <= 100,
                  ),
                acquiredCalc: Yup.string().default('').defined(),
                employees: Yup.string()
                  .required(ERROR_MESSAGES.INVALID_EMPLOYEES)
                  .matches(REGEXPS.INTEGER, ERROR_MESSAGES.INVALID_EMPLOYEES),
                ceo: Yup.object({
                  name: Yup.string()
                    .default('')
                    .defined()
                    .max(500, ERROR_MESSAGES.max('CEO Name', 500)),
                  education: autocompleteOptionSchema.nullable(),
                  biography: Yup.string()
                    .default('')
                    .defined()
                    .max(2000, ERROR_MESSAGES.max('CEO Biography', 2000)),
                  educationHistory: Yup.string().default('').defined(),
                }),
                ceoIsFounder: Yup.boolean().defined(),
                ceoChanged: Yup.boolean().defined(),
                generatingRevenue: Yup.boolean().defined(),
                participatingPreferred: Yup.boolean().defined(),
                noAnnouncement: Yup.boolean().defined(),
                revenueStatusChanged: Yup.boolean().defined(),
                generatingProfit: Yup.boolean().defined(),
                investors: Yup.array()
                  .of(
                    Yup.object({
                      id: Yup.mixed<string | number>()
                        .required()
                        .test(
                          'string-or-number',
                          '',
                          value => typeof value === 'string' || typeof value === 'number',
                        ),
                      pbId: Yup.string().default('').defined().nullable(),
                      name: Yup.string().required(),
                      lead: Yup.boolean().defined(),
                      new: Yup.boolean().defined(),
                      corporate: Yup.boolean().defined(),
                    }),
                  )
                  .required(ERROR_MESSAGES.MIN_INVESTORS)
                  .min(1, ERROR_MESSAGES.MIN_INVESTORS),
              }).test(
                'announcedDate-should-be',
                ERROR_MESSAGES.INVALID_DATE,
                ({ announcedDate, noAnnouncement }, testContext) => {
                  if (!noAnnouncement && announcedDate === null) {
                    return testContext.createError({
                      path: `${testContext.path}.announcedDate`,
                      message: ERROR_MESSAGES.INVALID_DATE,
                    });
                  }

                  return true;
                },
              ),
            )
            .required(),
        }),
      )
      .required(),
  }).test('form-validation', '', ({ foundedYear, firstFinancingDate, rounds }, testContext) => {
    const errors: Yup.ValidationError[] = [];

    // First Financing Date Validation
    if (
      firstFinancingDate &&
      foundedYear &&
      !isBefore(new Date(+foundedYear - 1, 11, 31), startOfDay(firstFinancingDate))
    ) {
      errors.push(
        testContext.createError({
          path: `${testContext.path}.firstFinancingDate`,
          message: ERROR_MESSAGES.INVALID_FIRST_FINANCING_DATE,
        }),
      );
    }

    for (let roundIndex = 0; roundIndex < rounds.length; roundIndex++) {
      for (
        let subroundIndex = 0;
        subroundIndex < rounds[roundIndex].subrounds.length;
        subroundIndex++
      ) {
        const currentSubround = rounds[roundIndex].subrounds[subroundIndex];

        const previousRound = rounds[roundIndex - 1] || null;
        const previousSubround =
          rounds[roundIndex].subrounds[subroundIndex - 1] ||
          previousRound?.subrounds[(previousRound?.subrounds.length || 0) - 1] ||
          null;

        // Deal Date Validation
        if (roundIndex === 0 && subroundIndex === 0) {
          // For First Subround
          if (
            firstFinancingDate &&
            currentSubround.dealDate &&
            isBefore(startOfDay(currentSubround.dealDate), startOfDay(firstFinancingDate))
          ) {
            errors.push(
              testContext.createError({
                path: `${testContext.path}.rounds.${roundIndex}.subrounds.${subroundIndex}.dealDate`,
                message: () => ERROR_MESSAGES.DEAL_DATE_AFTER_FINANCING_DATE,
              }),
            );
          }
          // For Other Subrounds
        } else if (
          previousSubround?.dealDate &&
          currentSubround.dealDate &&
          !isBefore(startOfDay(previousSubround?.dealDate), startOfDay(currentSubround.dealDate))
        ) {
          errors.push(
            testContext.createError({
              path: `${testContext.path}.rounds.${roundIndex}.subrounds.${subroundIndex}.dealDate`,
              message: ERROR_MESSAGES.DEAL_DATE_AFTER_PREVIOUS_DEAL_DATE,
            }),
          );
        }

        // Announced Date Validation
        if (
          currentSubround.dealDate &&
          currentSubround.announcedDate &&
          isBefore(startOfDay(currentSubround.dealDate), startOfDay(currentSubround.announcedDate))
        ) {
          errors.push(
            testContext.createError({
              path: `${testContext.path}.rounds.${roundIndex}.subrounds.${subroundIndex}.announcedDate`,
              message: ERROR_MESSAGES.ANNOUNCED_DATE_AFTER_DEAL_DATE,
            }),
          );
        }

        // CEO Name and Education Validations
        if (roundIndex === 0 || currentSubround.ceoChanged) {
          // CEO Name Validation
          if (currentSubround.ceo.name.length < 1) {
            errors.push(
              testContext.createError({
                path: `${testContext.path}.rounds.${roundIndex}.subrounds.${subroundIndex}.ceo.name`,
                message: ERROR_MESSAGES.INVALID_CEO_NAME,
              }),
            );
          }

          // CEO Education Validation
          if (!currentSubround.ceo.education) {
            errors.push(
              testContext.createError({
                path: `${testContext.path}.rounds.${roundIndex}.subrounds.${subroundIndex}.ceo.education`,
                message: ERROR_MESSAGES.INVALID_CEO_EDUCATION,
              }),
            );
          }
        }

        // Round C Security Type, CAP, Discount, Interest Validations
        if (roundIndex === 2) {
          // Security Type Validation
          if (!currentSubround.securityType) {
            errors.push(
              testContext.createError({
                path: `${testContext.path}.rounds.${roundIndex}.subrounds.${subroundIndex}.securityType`,
                message: ERROR_MESSAGES.MIN_SELECT,
              }),
            );
          }

          // CAP Validation
          if (
            (currentSubround.securityType?.value === 2 ||
              currentSubround.securityType?.value === 3) &&
            currentSubround.cap.length < 1
          ) {
            errors.push(
              testContext.createError({
                path: `${testContext.path}.rounds.${roundIndex}.subrounds.${subroundIndex}.cap`,
                message: ERROR_MESSAGES.INVALID_POSITIVE_MONEY,
              }),
            );
          }

          // Discount Validation
          if (
            (currentSubround.securityType?.value === 2 ||
              currentSubround.securityType?.value === 3) &&
            currentSubround.discount.length < 1
          ) {
            errors.push(
              testContext.createError({
                path: `${testContext.path}.rounds.${roundIndex}.subrounds.${subroundIndex}.discount`,
                message: ERROR_MESSAGES.INVALID_PERCENTAGE,
              }),
            );
          }

          // Interest Validation
          if (currentSubround.securityType?.value === 3 && currentSubround.interest.length < 1) {
            errors.push(
              testContext.createError({
                path: `${testContext.path}.rounds.${roundIndex}.subrounds.${subroundIndex}.interest`,
                message: ERROR_MESSAGES.INVALID_PERCENTAGE,
              }),
            );
          }
        }
      }
    }

    return errors.length === 0 || new Yup.ValidationError(errors);
  }),
});

const userBaseSchema = {
  firstName: Yup.string().required(ERROR_MESSAGES.required('First Name')),
  lastName: Yup.string().required(ERROR_MESSAGES.required('Last Name')),
  email: emailSchema,
  position: autocompleteOptionSchema
    .nullable()
    .test('value-is-set', ERROR_MESSAGES.MIN_SELECT, value => value !== null),
  role: autocompleteOptionSchema
    .nullable()
    .test('value-is-set', ERROR_MESSAGES.MIN_SELECT, value => value !== null),
};

export const createUserFormSchema = Yup.object({
  ...userBaseSchema,
  password: passwordSchema.min(8, ERROR_MESSAGES.min('Password', 8)),
  confirmPassword: Yup.string()
    .required(ERROR_MESSAGES.required('Confirm Password'))
    .oneOf([Yup.ref('password')], ERROR_MESSAGES.PASSWORDS_MISMATCH),
});

export const editUserFormSchema = Yup.object({
  ...userBaseSchema,
  password: Yup.string().min(8, ERROR_MESSAGES.min('Password', 8)),
  confirmPassword: Yup.string().test('passwords-match', (value, context) => {
    const password = context.parent.password;

    if (password !== value) {
      return context.createError({
        message: ERROR_MESSAGES.PASSWORDS_MISMATCH,
      });
    }

    return true;
  }),
});

export const selectCompanyFormSchema = Yup.object({
  company: autocompleteOptionSchema
    .nullable()
    .test('value-is-set', ERROR_MESSAGES.MIN_SELECT, value => value !== null),
});

export const generateLinkFormSchema = Yup.object({
  email: emailSchema,
  ccEmails: Yup.array()
    .defined()
    .of(autocompleteOptionSchema.typeError(ERROR_MESSAGES.INVALID_EMAIL))
    .test('valid-emails', ERROR_MESSAGES.INVALID_EMAIL, value => {
      let isValid = true;

      value.forEach(option => {
        const email = option.value;

        if (!Yup.string().email().isValidSync(email)) {
          isValid = false;
        }

        return true;
      });

      return isValid;
    }),
});
